import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './AdminPortfolio.css';

interface PortfolioProject {
  id: number;
  title: string;
  category: string;
  location: string;
  description: string;
  imageData: string | null;
  imageMime: string | null;
  imageUrl: string | null;
  order: number;
}

const CATEGORIES = ['Civil & Interior', 'Civil Works', 'Interior Design', 'Consultation'];

const emptyForm = { title: '', category: 'Civil & Interior', location: '', description: '', order: 0, imageUrl: '' };

const AdminPortfolio: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get('/portfolio');
      setProjects(res.data);
    } catch {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview('');
    setShowForm(true);
  };

  const openEdit = (p: PortfolioProject) => {
    setEditId(p.id);
    setForm({
      title: p.title,
      category: p.category,
      location: p.location,
      description: p.description,
      order: p.order,
      imageUrl: p.imageUrl || '',
    });
    setImagePreview(
      p.imageData
        ? `data:${p.imageMime};base64,${p.imageData}`
        : p.imageUrl || ''
    );
    setImageFile(null);
    setShowForm(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('category', form.category);
      fd.append('location', form.location);
      fd.append('description', form.description);
      fd.append('order', String(form.order));
      fd.append('imageUrl', form.imageUrl || '');
      if (imageFile) fd.append('image', imageFile);

      if (editId) {
        await api.put(`/portfolio/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/portfolio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowForm(false);
      fetchProjects();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this project from the portfolio?')) return;
    try {
      await api.delete(`/portfolio/${id}`);
      fetchProjects();
    } catch {
      setError('Failed to delete project');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Load the 6 default sample projects so you can edit them?')) return;
    try {
      await api.post('/portfolio/seed-defaults');
      fetchProjects();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load defaults');
    }
  };

  return (
    <div className="ap">
      {/* Header */}
      <div className="ap__header">
        <div className="ap__header-left">
          <button className="ap__back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1>Portfolio Manager</h1>
          <p>Manage the projects shown on your public website</p>
        </div>
        <button className="ap__add-btn" onClick={openAdd}>+ Add Project</button>
      </div>

      {error && <div className="ap__error">{error}</div>}

      {/* Project Grid */}
      {loading ? (
        <div className="ap__loading">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="ap__empty">
          <span>🖼️</span>
          <h3>No portfolio projects yet</h3>
          <p>Add your first project to showcase on the website</p>
          <div className="ap__empty-actions">
            <button type="button" className="ap__add-btn" onClick={openAdd}>+ Add First Project</button>
            <button type="button" className="ap__seed-btn" onClick={handleSeedDefaults}>
              📥 Load 6 Sample Projects
            </button>
          </div>
          <small className="ap__empty-hint">
            Loads the same Modern Villa, Corporate Office, Boutique Hotel, etc. shown on your public site — you can then edit them.
          </small>
        </div>
      ) : (
        <div className="ap__grid">
          {projects.map(p => (
            <div key={p.id} className="ap__card">
              <div className="ap__card-img">
                {p.imageData
                  ? <img src={`data:${p.imageMime};base64,${p.imageData}`} alt={p.title} />
                  : p.imageUrl
                    ? <img src={p.imageUrl} alt={p.title} />
                    : <div className="ap__card-no-img">No Image</div>
                }
                <span className="ap__card-cat">{p.category}</span>
              </div>
              <div className="ap__card-body">
                <h4>{p.title}</h4>
                <p className="ap__card-loc">📍 {p.location}</p>
                <p className="ap__card-desc">{p.description}</p>
              </div>
              <div className="ap__card-actions">
                <button className="ap__edit-btn" onClick={() => openEdit(p)}>✏️ Edit</button>
                <button className="ap__del-btn" onClick={() => handleDelete(p.id)}>🗑️ Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <div className="ap__modal-overlay" onClick={() => setShowForm(false)}>
          <div className="ap__modal" onClick={e => e.stopPropagation()}>
            <div className="ap__modal-header">
              <h2>{editId ? 'Edit Project' : 'Add New Project'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="ap__form">

              {/* Image Upload */}
              <div className="ap__img-upload" onClick={() => fileRef.current?.click()}>
                {imagePreview
                  ? <img src={imagePreview} alt="Preview" />
                  : <div className="ap__img-placeholder">
                      <span>📷</span>
                      <p>Click to upload photo</p>
                      <small>JPG, PNG, WEBP — max 5MB</small>
                    </div>
                }
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
              </div>
              {imagePreview && (
                <button type="button" className="ap__change-img" onClick={() => fileRef.current?.click()}>
                  Change Photo
                </button>
              )}

              <div className="ap__form-row">
                <div className="ap__field">
                  <label>Project Title *</label>
                  <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Modern Villa" />
                </div>
                <div className="ap__field">
                  <label>Category *</label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="ap__form-row">
                <div className="ap__field">
                  <label>Location</label>
                  <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Chennai" />
                </div>
                <div className="ap__field">
                  <label>Display Order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.order}
                    onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="ap__field">
                <label>Image URL (alternative to upload)</label>
                <input
                  type="url"
                  value={form.imageUrl}
                  onChange={e => setForm({...form, imageUrl: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                />
                <small>Paste an image URL if you don't want to upload a file. Uploading a file will override the URL.</small>
              </div>

              <div className="ap__field">
                <label>Description</label>
                <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description of the project..." />
              </div>

              {error && <div className="ap__error">{error}</div>}

              <div className="ap__form-actions">
                <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="ap__save-btn" disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPortfolio;
